<template>
  <div ref="regulatoryQuestions">
    <div id="regulatoryQuestions" class="application-section">
      <section id="regulatorySection">
        <div class="container is-fluid">
          <ChooseQuestion :question="componentData.question"
                          :options="componentData.options"
                          :existingValue="insurance.regulatoryProceedings ? componentData.options[1].text : componentData.options[0].text"
                          :class="{error: $v.insurance.regulatoryProceedings.$error}"
                          @question-answered="regulatoryProceedingsAnswered"
                          :isMandatory="true"
                          :isDropdownControlled="false"
                          :tab="sections.claimSection.tab" />
          <div v-if="hasRegulatoryProceedings" class="claim-details">
            <span class="extra-question-label">{{componentData.detailsFurtherInformation}}</span>
            <b-input v-model.trim="$v.insurance.regulatoryProceedingsInfo.$model"
                     :placeholder="componentData.placeholder"
                     :class="[{mandatory : hasRegulatoryProceedings},
                       {error: $v.insurance.regulatoryProceedingsInfo.$error}, 'new-line', 'question-control','form-label']"
                     :tabindex="sections.regulatorySection.tab"
                     @blur="infoUpdated"
                     type="textarea"
                     maxlength="8000">
            </b-input>
          </div>
        </div>
      </section>
      <section id="claimSection" :class="sections.claimSection" v-if="!componentData.optedOut">
        <div class="container is-fluid">
          <ChooseQuestion :question="componentData.claimsQuestion"
                          :options="componentData.options"
                          :existingValue="insurance.claimsInLastFiveYears ? componentData.options[1].text : componentData.options[0].text"
                          :class="{error: $v.insurance.claimsInLastFiveYears.$error}"
                          @question-answered="claimsAnswered"
                          :isMandatory="true"
                          :isDropdownControlled="true"
                          :tab="sections.claimSection.tab" />
          <div v-if="hasClaims" class="claim-details">
            <span class="extra-question-label">{{componentData.detailsFurtherInformation}}</span>
            <b-input v-model.trim="$v.insurance.claimsInLastFiveYearsInfo.$model"
                     :placeholder="componentData.placeholder"
                     :class="[{mandatory : hasClaims},
                        {error: $v.insurance.claimsInLastFiveYearsInfo.$error}, 'new-line', 'question-control','form-label']"
                     :tabindex="sections.claimSection.tab"
                     @blur="infoUpdated"
                     type="textarea"
                     maxlength="8000">
            </b-input>
          </div>
          <p v-html="componentData.information" class="disclaimer-poppin" v-if="answeredYesToEitherNotOptedOutDisclaimer"></p>
          <p v-html="componentData.furtherInformation" class="disclaimer-poppin"></p>
          <p v-html="componentData.tncsReminder" class="disclaimer-poppin"></p>
        </div>
      </section>
      <div id="component-buttons" v-if="!isMobile">
        <div v-if="!componentData.retiredOrOverseas">
          <button @click.stop.prevent="cancel" type="button" style="cursor:pointer" class="form-button back-button">
          {{componentData.backButton}}</button>
        </div>
        <div>
          <button v-on:click="confirm" type="button" style="cursor:pointer" class="form-button next-button">
          {{componentData.nextButton}}</button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
/* eslint-disable no-unused-expressions */
import ChooseQuestion from '@/components/ChooseQuestion.vue';
import { mapState, mapGetters } from 'vuex';
import {
  required,
  requiredIf,
} from 'vuelidate/lib/validators';
import '@/styles/application.scss';

export default {
  name: 'RegulatoryQuestions',
  components: {
    ChooseQuestion,
  },
  props: {
    componentData: Object,
  },
  data() {
    return {
      insurance: {},
      sections: {
        regulatorySection: { tab: this.$store.state.isMobile ? -1 : 0 },
        claimSection: { tab: this.$store.state.isMobile ? -1 : 0 },
      },
    };
  },
  created() {
    this.insurance = this.getInsurance;
  },
  computed: {
    ...mapGetters(['getInsurance', 'isFinalSection']),
    ...mapState(['isMobile', 'application']),
    hasRegulatoryProceedings() {
      return this.insurance.regulatoryProceedings === this.componentData.options[1].text;
    },
    hasClaims() {
      return this.insurance.claimsInLastFiveYears === this.componentData.options[1].text;
    },
    answeredYesToEitherNotOptedOutDisclaimer() {
      return !this.componentData.optedOut && (this.hasClaims || this.hasRegulatoryProceedings);
    },
  },
  validations() {
    return {
      insurance: {
        regulatoryProceedings: {
          required,
        },
        regulatoryProceedingsInfo: {
          required: requiredIf(() => this.hasRegulatoryProceedings),
        },
        claimsInLastFiveYears: {
          required: requiredIf(() => !this.componentData.optedOut),
        },
        claimsInLastFiveYearsInfo: {
          required: requiredIf(() => this.hasClaims),
        },
      },
    };
  },
  methods: {
    confirm() {
      this.$v.$touch();
      if (this.$v.$anyError) {
        this.$nextTick(() => {
          // - 100 is offset because of Sticky positional DNN header.
          window.scrollTo(0, (document.querySelector('.error').getBoundingClientRect().top - 100) + window.pageYOffset);
        });
        return;
      }

      this.raiseRegulatoryChanged();

      this.$emit('completed', { process: this.componentData.process });
    },
    cancel() {
      this.$emit('returnToOptOut', this.insurance);
    },
    regulatoryProceedingsAnswered(reg) {
      this.insurance.regulatoryProceedings = reg.text;
      this.$v.insurance.regulatoryProceedings.$touch();
      this.raiseRegulatoryChanged();
    },
    claimsAnswered(claim) {
      this.insurance.claimsInLastFiveYears = claim.text;
      this.raiseRegulatoryChanged();
    },
    infoUpdated() {
      this.raiseRegulatoryChanged();
    },
    async HasErrorInSection(currentSection) {
      if (currentSection === 'regulatorySection') {
        this.$v.insurance.regulatoryProceedings.$touch();
        this.$v.insurance.regulatoryProceedingsInfo.$touch();
        return this.$v.insurance.regulatoryProceedings.$error || this.$v.insurance.regulatoryProceedingsInfo.$error;
      }
      if (currentSection === 'claimSection') {
        this.$v.insurance.claimsInLastFiveYears.$touch();
        this.$v.insurance.claimsInLastFiveYears.$touch();
        return this.$v.insurance.claimsInLastFiveYears.$error
          || this.$v.insurance.claimsInLastFiveYearsInfo.$error;
      }
      return false;
    },
    raiseRegulatoryChanged() {
      this.$emit('regulatoryChanged', this.insurance);
    },
  },
};
</script>
<style lang="scss">
  @import '../styles/_mixins.scss';
  #regulatoryQuestions {
    .claim-details {
      margin-top: 20px;
      font-size: 18px;

      div.new-line {
        display: flex;
        margin-top: 2%;
        margin-left: 0;

        textarea.textarea {
          font-size: 14px;
        }

        input {
          width: 95%;
          padding: 0;
          color: $purple;
          border-bottom: 2px solid $darkpurple;
        }
      }
    }

    #component-buttons {
      display: inline-flex;
    }

    p.disclaimer-poppin {
      @include DefaultPoppinFont();
    }
  }
</style>
